import { useState } from "react";
import { toast } from "sonner";
import { Loader2, SlidersHorizontal, Send } from "lucide-react";
import { BACKEND_URL, formatError } from "@/lib/api";
import { encodeWav24 } from "@/lib/mastering";
import { MasteringStudio } from "@/components/admin/MasteringStudio";

const CHUNK = 6 * 1024 * 1024;

async function uploadMaster(o, t, blob, onProgress) {
  const uploadId = Array.from(crypto.getRandomValues(new Uint8Array(12)), (b) => b.toString(16).padStart(2, "0")).join("");
  const total = Math.ceil(blob.size / CHUNK);
  let res;
  for (let i = 0; i < total; i++) {
    const fd = new FormData();
    fd.append("t", t); fd.append("upload_id", uploadId); fd.append("index", i); fd.append("total", total);
    fd.append("file", blob.slice(i * CHUNK, (i + 1) * CHUNK), "chunk.bin");
    const r = await fetch(`${BACKEND_URL}/api/orders/${o.id}/master`, { method: "POST", body: fd });
    res = await r.json().catch(() => ({}));
    if (!r.ok) throw { response: { data: res } };
    onProgress(Math.round(((i + 1) / total) * 100));
  }
  return res;
}

export const CustomerMastering = ({ o, t, onDelivered }) => {
  const [source, setSource] = useState(null);
  const [busy, setBusy] = useState("");
  const open = async () => {
    setBusy("Caricamento del tuo brano…");
    try {
      const r = await fetch(`${BACKEND_URL}/api/orders/${o.id}/source?t=${encodeURIComponent(t)}`);
      if (!r.ok) throw new Error();
      setSource({ arrayBuffer: await r.arrayBuffer(), name: o.track_title });
    } catch { toast.error("Impossibile caricare il brano dell'ordine"); }
    finally { setBusy(""); }
  };
  const deliver = async (result) => {
    setBusy("Invio del master: 0%");
    try {
      await uploadMaster(o, t, encodeWav24(result.buffer), (p) => setBusy(`Invio del master: ${p}%`));
      toast.success("Master salvato! Ti abbiamo inviato il link via email.");
      onDelivered();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(""); }
  };
  return (
    <div className="rounded-2xl border border-emerald-400/30 bg-[#1e2236] p-5 sm:p-6 space-y-4" data-testid="customer-mastering">
      <p className="font-display text-lg font-bold uppercase inline-flex items-center gap-2"><SlidersHorizontal className="h-5 w-5 text-emerald-300" /> Studio di mastering online</p>
      <p className="text-sm text-zinc-400">Masterizza il tuo brano con la nostra catena da studio (EQ, compressione multibanda, saturazione analogica, limiter true peak). Prova tutte le volte che vuoi, poi salva il master: lo trovi qui e ti arriva via email. Puoi salvare fino a {o.masters_left ?? 5} versioni.</p>
      {!source && <button onClick={open} disabled={!!busy} className="inline-flex items-center gap-2 rounded-full bg-emerald-500 text-[#151827] px-6 py-3 font-bold disabled:opacity-60" data-testid="customer-mastering-open-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <SlidersHorizontal className="h-4 w-4" />} Apri lo studio</button>}
      {busy && source && <p className="text-sm text-emerald-300 inline-flex items-center gap-2" data-testid="customer-mastering-status"><Loader2 className="h-4 w-4 animate-spin" /> {busy}</p>}
      {source && <MasteringStudio source={source} allowUpload={false} actions={(result) => (
        <button onClick={() => deliver(result)} disabled={!!busy || !(o.masters_left > 0)} className="inline-flex items-center gap-2 rounded-full bg-hot text-[#151827] px-6 py-3 font-bold disabled:opacity-50" data-testid="customer-mastering-deliver-btn">
          <Send className="h-4 w-4" /> {o.masters_left > 0 ? "Salva questo master nel mio ordine" : "Limite versioni raggiunto"}
        </button>
      )} />}
    </div>
  );
};
